import { SOCIAL_AD_TYPES } from "utils/constants";
import { KILO, MEGA, SECONDS_PER_MIN } from "utils/common";

import { isURL, maxLength, requiredNotNull } from "widgets/forms/validators/validators";
import { creativeNameAllowedChars, creativeNameMaxLength } from "widgets/forms/validators/Creatives";
import { filesize, duration, dimensions } from "widgets/forms/validators/Assets";
import {
	errorMessageReducer,
	minCards,
	makeDynamicConfig,
	cardValidator,
	validateUnlessAdStudio,
	requiredAsset
} from "./validators";

import {
	uniqueVariableName,
	uniqueCardVariableName,
	mergeLists
} from "./utils";

/**
 * LinkedIn Image
 ********************************************/

export const LINKEDIN_IMAGE_CONSTRAINTS = {
	MAX_LENGTH_HEADLINE: 200,
	MAX_LENGTH_INTRO: 600,
	MAX_LENGTH_LANDING_PAGE: 800,
	MAX_IMAGE_MB: 5,
	IMAGE_ACCEPT: ".jpg, .jpeg, .png, .gif",
	DIMENSIONS: {
		minWidth: 360,
		minHeight: 360,
	},
};

export const LINKEDIN_IMAGE_VALIDATORS = {
	headline: [
		requiredNotNull,
		maxLength(LINKEDIN_IMAGE_CONSTRAINTS.MAX_LENGTH_HEADLINE),
	],
	intro: [
		requiredNotNull,
		maxLength(LINKEDIN_IMAGE_CONSTRAINTS.MAX_LENGTH_INTRO),
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(LINKEDIN_IMAGE_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	image: validateUnlessAdStudio([
		requiredAsset,
		filesize({
			max: LINKEDIN_IMAGE_CONSTRAINTS.MAX_IMAGE_MB * MEGA,
		}),
		dimensions(LINKEDIN_IMAGE_CONSTRAINTS.DIMENSIONS),
	]),
};

const LINKEDIN_IMAGE_WARNINGS = mergeLists(
	{
		headline: [uniqueVariableName],
		intro: [uniqueVariableName],
		landingPage: [uniqueVariableName],
		image: [uniqueVariableName],
	},
	makeDynamicConfig(LINKEDIN_IMAGE_VALIDATORS)
);

export function linkedInImageWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.LINKEDIN_IMAGE;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(LINKEDIN_IMAGE_WARNINGS, values, formName);
	}
}

export function linkedInImageValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.LINKEDIN_IMAGE;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...LINKEDIN_IMAGE_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}


/**
 * LinkedIn Video
 ********************************************/

export const LINKEDIN_VIDEO_CONSTRAINTS = {
	MAX_LENGTH_HEADLINE: 200,
	MAX_LENGTH_INTRO: 600,
	MAX_LENGTH_LANDING_PAGE: 800,
	MIN_VIDEO_KB: 75,
	MAX_VIDEO_MB: 200,
	MIN_VIDEO_SEC: 3,
	MAX_VIDEO_MIN: 15,
	VIDEO_ACCEPT: ".mp4",
	THUMBNAIL_ACCEPT: ".jpg, .jpeg, .png",
	MAX_THUMBNAIL_MB: 2,
};

const DURATION = {
	min: LINKEDIN_VIDEO_CONSTRAINTS.MIN_VIDEO_SEC,
	max: LINKEDIN_VIDEO_CONSTRAINTS.MAX_VIDEO_MIN * SECONDS_PER_MIN,
};

export const LINKEDIN_VIDEO_VALIDATORS = {
	video: validateUnlessAdStudio([
		requiredAsset,
		filesize({
			min: LINKEDIN_VIDEO_CONSTRAINTS.MIN_VIDEO_KB * KILO,
			max: LINKEDIN_VIDEO_CONSTRAINTS.MAX_VIDEO_MB * MEGA,
		}),
		duration(DURATION),
	]),
	headline: [
		requiredNotNull,
		maxLength(LINKEDIN_VIDEO_CONSTRAINTS.MAX_LENGTH_HEADLINE),
	],
	intro: [
		requiredNotNull,
		maxLength(LINKEDIN_VIDEO_CONSTRAINTS.MAX_LENGTH_INTRO),
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(LINKEDIN_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	cta: [
		requiredNotNull,
	],
	thumbnail: validateUnlessAdStudio([
		filesize({ max: LINKEDIN_VIDEO_CONSTRAINTS.MAX_THUMBNAIL_MB * MEGA })
	]),
};

const LINKEDIN_VIDEO_WARNINGS = makeDynamicConfig(LINKEDIN_VIDEO_VALIDATORS);

export function linkedInVideoWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.LINKEDIN_VIDEO;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(LINKEDIN_VIDEO_WARNINGS, values, formName);
	}
}

export function linkedInVideoValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.LINKEDIN_VIDEO;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...LINKEDIN_VIDEO_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName, props);
}


/**
 * LinkedIn Carousel
 ********************************************/

export const LINKEDIN_CAROUSEL_CONSTRAINTS = {
	MAX_LENGTH_INTRO_DESCRIPTION: 255,
	MAX_LENGTH_LANDING_PAGE: 800,
	MAX_LENGTH_HEADLINE: 745,

	MAX_IMAGE_MB: LINKEDIN_IMAGE_CONSTRAINTS.MAX_IMAGE_MB,
	DIMENSIONS: LINKEDIN_IMAGE_CONSTRAINTS.DIMENSIONS,
	IMAGE_ACCEPT: LINKEDIN_IMAGE_CONSTRAINTS.IMAGE_ACCEPT,
};

export const LINKEDIN_CAROUSEL_CARD_VALIDATORS = {
	image: validateUnlessAdStudio([
		requiredAsset,
		filesize({ max: LINKEDIN_CAROUSEL_CONSTRAINTS.MAX_IMAGE_MB * MEGA }),
		dimensions(LINKEDIN_CAROUSEL_CONSTRAINTS.DIMENSIONS),
	]),
	headline: [
		requiredNotNull,
		maxLength(LINKEDIN_CAROUSEL_CONSTRAINTS.MAX_LENGTH_HEADLINE)
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(LINKEDIN_CAROUSEL_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE)
	],
};

const LINKEDIN_CAROUSEL_CARD_WARNINGS = mergeLists(
	{
		image: [uniqueCardVariableName],
		headline: [uniqueCardVariableName],
		landingPage: [uniqueCardVariableName],
	},
	makeDynamicConfig({
		...LINKEDIN_CAROUSEL_CARD_VALIDATORS,
	})
);

export const LINKEDIN_CAROUSEL_VALIDATORS = {
	introDescription: [
		requiredNotNull,
		maxLength(LINKEDIN_CAROUSEL_CONSTRAINTS.MAX_LENGTH_INTRO_DESCRIPTION),
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(LINKEDIN_CAROUSEL_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	cards: [
		cardValidator(LINKEDIN_CAROUSEL_CARD_WARNINGS),
	],
	cardCount: [
		minCards,
	],
};

const LINKEDIN_CAROUSEL_WARNINGS = mergeLists(
	{
		introDescription: [uniqueVariableName],
		landingPage: [uniqueVariableName],
	},
	makeDynamicConfig({
		...LINKEDIN_CAROUSEL_VALIDATORS,
	})
);

export function linkedInCarouselWarning(allValues, props){
	const formName = SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(LINKEDIN_CAROUSEL_WARNINGS, values, formName, props);
	}
}

export function linkedInCarouselValidator(allValues, props){
	const formName = SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...LINKEDIN_CAROUSEL_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName, props);
}
